import { loadSeasonData } from '../seasonData'

/**
 * "The world moved — re-read it".
 *
 * A replica clock control changes the fixtures, their statuses, the standings and the derived
 * phase. Without this, a jump that worked perfectly still shows the pre-jump app until the user
 * reloads by hand, which is most of why the tools look like they "did nothing".
 *
 * `resetAll` reloads the page, which for a control pressed once in a while is the honest fix. The
 * clock controls are pressed repeatedly and need the cheaper path: re-fetch the season store and
 * tell React it changed.
 *
 * A DOM event rather than a prop, deliberately. The rail lives four components below `App`, and
 * threading a `bumpSeasonVersion` callback through `GameweekGrid` → `GameweekDevTools` →
 * `ReplicaSection` would put a dev-only concern in three components' prop types. The season store
 * is already a module-level singleton outside React (`seasonData.ts`) — this is its change
 * notification, and it stays in exactly one place.
 */

export const WORLD_CHANGED_EVENT = 'rwbet:world-changed'

/** Subscribes to world changes. Returns the unsubscribe, so it drops straight into a `useEffect`. */
export function onWorldChanged(listener: () => void): () => void {
  window.addEventListener(WORLD_CHANGED_EVENT, listener)
  return () => window.removeEventListener(WORLD_CHANGED_EVENT, listener)
}

/**
 * Re-fetches fixtures and results, then announces it.
 *
 * The announcement fires even when the fetch fails: the phase poll and the live poll are on their
 * own cadences and a re-render against slightly stale fixtures is better than a rail control that
 * silently gives up. Failures are logged, never thrown at the caller — this is a refresh, and the
 * action it follows has already happened.
 */
export async function refreshWorld(): Promise<void> {
  try {
    await loadSeasonData()
  } catch (error) {
    console.error('Failed to re-read the season after a dev-tool change', error)
  }
  window.dispatchEvent(new Event(WORLD_CHANGED_EVENT))
}
